import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useCreateCategory, useUpdateCategory } from '../../hooks/useCategories';
import type { CreateCategoryDto, Category, UpdateCategoryDto } from '../../types';
import { categoryFormSchema } from '../../utils/categoryValidation';
import { Button, ControlledInput, ControlledFileInput, ControlledCheckbox } from '@shared/ui';
import './CategoryForm.css';

interface CategoryFormProps {
  category?: Category;
  sectionId?: number;
  onSuccess?: () => void;
}

type FileInputValue = File | { id: number; url: string };

interface CategoryFormData extends CreateCategoryDto {
  image?: FileInputValue | null;
}

export default function CategoryForm({ category, sectionId: sectionIdProp, onSuccess }: CategoryFormProps) {
  const { sectionId: sectionIdParam } = useParams<{ sectionId?: string }>();
  const sectionIdFromUrl = sectionIdParam ? parseInt(sectionIdParam, 10) : undefined;
  const sectionId = sectionIdProp ?? sectionIdFromUrl;
  const isEditMode = !!category;

  // Проверяем, что sectionId валидное число
  const isValidSectionId = sectionId && !isNaN(sectionId);

  const resolver = yupResolver(categoryFormSchema) as any;

  const { handleSubmit, formState: { errors }, reset, control } = useForm<CategoryFormData>({
    resolver,
    context: { requireImage: !isEditMode },
    defaultValues: {
      name: category?.name || '',
      isNotFlower: category?.isNotFlower ?? false,
      sectionId: category?.sectionId,
      image: category?.imageUrl ? { id: 0, url: category.imageUrl } : null,
    },
  });
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();

  useEffect(() => {
    if (category) {
      reset({
        name: category.name,
        isNotFlower: category.isNotFlower ?? false,
        sectionId: category.sectionId,
        image: category.imageUrl ? { id: 0, url: category.imageUrl } : null,
      });
    }
  }, [category, reset]);

  const onSubmit = async (data: CategoryFormData) => {
    try {
      const imageFile = data.image instanceof File ? data.image : undefined;

      // Автоматически добавляем sectionId из пропсов или URL, если он есть и валидный (только при создании)
      if (isEditMode && category) {
        const updateData: UpdateCategoryDto = {
          name: data.name,
          isNotFlower: data.isNotFlower === true,
          sectionId: data.sectionId,
        };

        // Определяем, было ли изображение удалено
        // Если изначально было изображение, а сейчас null - значит удалено
        const hadImage = category.imageUrl && category.imageUrl.length > 0;
        const imageRemoved = hadImage && data.image === null;

        if (imageRemoved) {
          updateData.shouldDeleteImage = true;
        }

        await updateCategory.mutateAsync({
          id: category.id,
          data: updateData,
          file: imageFile
        });
        notifications.show({
          title: 'Успешно',
          message: 'Категория успешно обновлена',
          color: 'green',
        });
      } else {
        const createData: CreateCategoryDto = {
          name: data.name,
          isNotFlower: data.isNotFlower === true,
          sectionId: isValidSectionId ? sectionId : data.sectionId,
        };
        await createCategory.mutateAsync({ data: createData, file: imageFile });
        notifications.show({
          title: 'Успешно',
          message: 'Категория успешно создана',
          color: 'green',
        });
      }
      reset();
      onSuccess?.();
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: `Ошибка при ${isEditMode ? 'обновлении' : 'создании'} категории`,
        color: 'red',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="category-form">
      <div className="category-form__columns">
        <div className="category-form__left">
          <div className="form-group">
            <ControlledInput
              id="name"
              type="text"
              label="Название *"
              name="name"
              control={control}
              error={errors.name}
            />
          </div>
          <div className="form-group">
            <ControlledCheckbox label="Не цветы" name="isNotFlower" control={control} />
            <p className="category-form__hint">
              Для шаров, подарков и открыток. У товара этой категории не будет типа цветка и типа сборки, цвет останется.
            </p>
          </div>
        </div>

        <div className="category-form__right">
          <div className="form-group category-form__image">
            <ControlledFileInput
              id="image"
              name="image"
              label={`Изображение ${!isEditMode ? '*' : ''}`}
              description="Загружайте квадратные фото в формате 1:1 — так они корректно отображаются в каталоге."
              accept="image/*"
              control={control}
              error={errors.image}
            />
          </div>
        </div>
      </div>

      <Button
        type="submit"
        loading={createCategory.isPending || updateCategory.isPending}
        className="category-form__submit-button"
      >
        {isEditMode ? 'Обновить категорию' : 'Создать категорию'}
      </Button>
    </form>
  );
}

